/**
 * Code One – Abo-Geschenke im Warenkorb
 *
 * - Liegt ein Abo (Position mit Abo-Plan) im Warenkorb, wird der Shaker automatisch als
 *   Geschenk-Position hinzugefügt (Property _co_gift = shaker, ein Shaker pro Abo). Kostenlos wird er durch den
 *   automatischen Rabatt "Abo-Geschenk: Gratis Code One Shaker" (nur bei Abo).
 * - Wird das Abo entfernt, verschwindet auch der Shaker.
 * - Shaker und T-Shirt sind nicht einzeln kaufbar: Positionen dieser Produkte ohne Abo bzw. ohne
 *   Geschenk-Property werden entfernt.
 * - Pro Abo ein T-Shirt: Jedes Abo bekommt eine eigene Position (Menge 1) mit eigener
 *   Property "T-Shirt-Größe" (Auswahl in der Geschenk-Box). Ohne Größe ist der Checkout gesperrt,
 *   Express-Buttons (Shop Pay usw.) sind dann ausgeblendet.
 *
 * Konfiguration kommt aus window.CoAboGifts (siehe snippets/co-abo-gifts.liquid).
 */
import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const cfg = window.CoAboGifts || {};
const root = window.Shopify?.routes?.root || '/';
const SOURCE = 'co-abo-gifts';
const SIZE_KEY = cfg.sizeKey || 'T-Shirt-Größe';
const sizes = cfg.shirtSizes || [];
let syncing = false;
let syncPending = false; // Änderung während eines laufenden Abgleichs → danach erneut abgleichen

const isAbo = (item) => Boolean(item.selling_plan_allocation);
const isGift = (item) => item.properties && item.properties._co_gift === 'shaker';
const goodieProducts = (cfg.goodieProductIds || []).map(Number);
const isGoodie = (item) => goodieProducts.includes(item.product_id);

const sectionIds = () =>
  [...document.querySelectorAll('cart-items-component')].map((el) => el.dataset.sectionId).filter(Boolean);

async function post(url, body) {
  const res = await fetch(`${root}${url}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ ...body, sections: sectionIds().join(',') }),
  });
  return res.json();
}

/** Re-render der Warenkorb-Komponenten über das Standard-Event des Themes. */
async function announce(sections, lines) {
  const cart = await fetch(`${root}cart.js`).then((r) => r.json());
  const deferred = CartLinesUpdateEvent.createPromise();
  document.dispatchEvent(
    new CartLinesUpdateEvent({ action: 'update', context: 'cart', lines, promise: deferred.promise })
  );
  deferred.resolve({
    cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
    detail: { didError: false, items: cart.items, source: SOURCE, sections, itemCount: cart.item_count },
  });
  return cart;
}

async function sync() {
  if (!cfg.shakerVariantId) return;
  if (syncing) {
    syncPending = true;
    return;
  }
  syncing = true;
  syncPending = false;
  let again = false;
  try {
    const cart = await fetch(`${root}cart.js`).then((r) => r.json());
    const hasAbo = cart.items.some(isAbo);

    // Goodies einzeln (ohne Geschenk-Property) sind nicht kaufbar
    const strays = cart.items.filter((i) => isGoodie(i) && !isGift(i));
    if (strays.length) {
      const updates = Object.fromEntries(strays.map((g) => [g.key, 0]));
      const res = await post('cart/update.js', { updates });
      await announce(res.sections, strays.map((g) => ({ merchandiseId: String(g.variant_id), quantity: 0 })));
      again = true;
      return;
    }

    const gifts = cart.items.filter(isGift);

    // Ein Shaker pro Abo-Einheit
    const units = cart.items.filter(isAbo).reduce((n, i) => n + i.quantity, 0);

    if (hasAbo && gifts.length === 0) {
      const res = await post('cart/add.js', {
        items: [{ id: Number(cfg.shakerVariantId), quantity: units, properties: { _co_gift: 'shaker' } }],
      });
      if (!res.status) await announce(res.sections, [{ merchandiseId: String(cfg.shakerVariantId), quantity: units }]);
      return;
    }

    if (!hasAbo && gifts.length > 0) {
      const updates = Object.fromEntries(gifts.map((g) => [g.key, 0]));
      const res = await post('cart/update.js', { updates });
      await announce(res.sections, gifts.map((g) => ({ merchandiseId: String(g.variant_id), quantity: 0 })));
      return;
    }

    // Shaker-Menge = Anzahl Abos (Geschenk-Positionen neu schreiben, Keys können sich durch Rabatte ändern)
    const giftQty = gifts.reduce((n, g) => n + g.quantity, 0);
    if (hasAbo && giftQty !== units) {
      await post('cart/update.js', { updates: Object.fromEntries(gifts.map((g) => [g.key, 0])) });
      const res = await post('cart/add.js', {
        items: [{ id: Number(cfg.shakerVariantId), quantity: units, properties: { _co_gift: 'shaker' } }],
      });
      await announce(res.sections, [{ merchandiseId: String(cfg.shakerVariantId), quantity: units }]);
    }
  } catch (err) {
    console.warn('[co-abo-gifts]', err);
  } finally {
    syncing = false;
    if (again || syncPending) sync();
  }
}

/**
 * T-Shirt-Größe für eine Abo-Einheit speichern (ein Abo = ein T-Shirt).
 * Shopify teilt Abo-Positionen wegen des Shaker-Rabatts auf und vergibt dabei wechselnde Keys.
 * Deshalb werden alle Abo-Positionen neu geschrieben: je Größe eine Position.
 */
async function saveSize(select) {
  const index = Number(select.dataset.unit);
  const size = select.value;
  if (Number.isNaN(index) || !size) return;
  const cart = await fetch(`${root}cart.js`).then((r) => r.json());
  const abos = cart.items.filter(isAbo);
  // Reihenfolge wie in snippets/co-abo-gifts.liquid: nach Größe (S, M, L, XL), Abos ohne Größe zuletzt
  const order = (s) => (s && sizes.includes(s) ? sizes.indexOf(s) : s ? sizes.length : sizes.length + 1);
  const units = abos
    .flatMap((item) => Array.from({ length: item.quantity }, () => ({ item, size: item.properties?.[SIZE_KEY] || '' })))
    .sort((a, b) => order(a.size) - order(b.size));
  if (!units[index]) return;
  units[index].size = size;

  const groups = new Map();
  for (const { item, size: s } of units) {
    const plan = item.selling_plan_allocation.selling_plan.id;
    const id = `${item.variant_id}|${plan}|${s}`;
    if (!groups.has(id)) {
      const properties = { ...(item.properties || {}) };
      delete properties[SIZE_KEY];
      if (s) properties[SIZE_KEY] = s;
      groups.set(id, { id: item.variant_id, quantity: 0, selling_plan: plan, properties });
    }
    groups.get(id).quantity += 1;
  }

  document.querySelectorAll('[data-co-size]').forEach((el) => (el.disabled = true));
  try {
    await post('cart/update.js', { updates: Object.fromEntries(abos.map((i) => [i.key, 0])) });
    const res = await post('cart/add.js', { items: [...groups.values()] });
    await announce(res.sections, [...groups.values()].map((g) => ({ merchandiseId: String(g.id), quantity: g.quantity })));
  } finally {
    document.querySelectorAll('[data-co-size]').forEach((el) => (el.disabled = false));
  }
}

document.addEventListener('change', (e) => {
  const select = e.target.closest?.('[data-co-size]');
  if (select) saveSize(select);
});

/** Checkout nur mit gewählter T-Shirt-Größe. */
document.addEventListener(
  'click',
  (e) => {
    const btn = e.target.closest?.('button[name="checkout"], a[href$="/checkout"], [name="checkout"]');
    if (!btn) return;
    const box = [...document.querySelectorAll('[data-co-gifts]')].find((b) => b.offsetParent !== null);
    const select = [...(box?.querySelectorAll('[data-co-size]') || [])].find((s) => !s.value);
    if (select) {
      e.preventDefault();
      e.stopImmediatePropagation();
      (select.closest('.co-gifts__row') || box).querySelector('[data-co-size-error]')?.removeAttribute('hidden');
      select.focus();
      select.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }
  },
  true
);

/** Zähler (Warenkorb-Symbol & Drawer-Titel) ohne Geschenk-Positionen anzeigen. */
async function fixCounts() {
  const cart = await fetch(`${root}cart.js`).then((r) => r.json());
  const visible = cart.items.filter((i) => !isGift(i)).reduce((n, i) => n + i.quantity, 0);
  document.querySelectorAll('.cart-bubble__text-count').forEach((el) => {
    el.textContent = visible > 0 && visible < 100 ? String(visible) : '';
    el.classList.toggle('hidden', visible === 0);
  });
  document.querySelectorAll('[data-co-cart-count]').forEach((el) => {
    el.textContent = String(visible);
    el.classList.toggle('visually-hidden', visible === 0);
  });
  // Express-Checkout (Shop Pay usw.) erst, wenn jedes Abo eine T-Shirt-Größe hat
  const missing = cart.items.some((i) => isAbo(i) && !i.properties?.[SIZE_KEY]);
  document.documentElement.classList.toggle('co-size-missing', missing);
  requestAnimationFrame(updateDrawerSticky);
}

/**
 * Die Express-Buttons machen den Kassenbereich im Drawer deutlich höher. Wie assets/cart-drawer.js:
 * Ist er höher als die halbe Drawer-Höhe, nicht fixieren, sonst schrumpft die Produktliste auf einen Streifen.
 */
function updateDrawerSticky() {
  const dialog = document.querySelector('#cart-drawer dialog');
  const summary = dialog?.querySelector('.cart-drawer__summary');
  const drawerHeight = dialog?.getBoundingClientRect().height || 0;
  if (!summary || !drawerHeight) return;
  const ratio = summary.getBoundingClientRect().height / drawerHeight;
  dialog.setAttribute('cart-summary-sticky', ratio > 0.5 ? 'false' : 'true');
}

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  event.promise
    ?.then((result) => {
      setTimeout(fixCounts, 60);
      if (result?.detail?.source === SOURCE) return;
      sync();
    })
    .catch(() => {});
});

sync();
fixCounts();
